import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, Clock, Wrench, X } from "lucide-react";
import { SkeletonCard, buttonClasses } from "../../../components/ui";
import {
  BILLING_SETTINGS_PATH,
  ROUTES,
  billingCheckoutPath,
} from "../../../routes/paths";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { formatCreatedOn } from "../../brand/planFormat";
import TransactionDetails, {
  TransactionRow,
  TransactionTone,
} from "../components/TransactionDetails";
import { CheckoutOutcome, clearResult, readResult } from "../checkoutOutcome";
import { CYCLE_LABELS, PRODUCT_NAME } from "../data/subscriptionPlans";
import { usePlans } from "../hooks/usePlans";
import { METHOD_LABELS } from "../subscriptionState";
import { formatMoney, priceBreakdown } from "../pricing";
import {
  SUBSCRIPTIONS_LIVE,
  fetchSubscription,
} from "../services/subscriptionsService";
import type { Subscription } from "../types/subscriptions.types";
import type { BillingCycleId } from "../types/billing.types";

/** The mark at the top, and the words under it. */
const FACE: Record<
  CheckoutOutcome["state"],
  {
    Icon: React.FC<{ className?: string; "aria-hidden"?: boolean }>;
    tint: string;
    title: string;
    tone: TransactionTone;
  }
> = {
  active: {
    Icon: Check,
    tint: "bg-brand-soft text-brand-dark",
    title: "Payment successful",
    tone: "paid",
  },
  pending: {
    Icon: Clock,
    tint: "bg-[#FFF6E5] text-[#9A6700]",
    title: "We're confirming your payment",
    tone: "pending",
  },
  failed: {
    Icon: X,
    tint: "bg-[#FDECEC] text-[#D14343]",
    title: "Payment failed",
    tone: "failed",
  },
  unavailable: {
    Icon: Wrench,
    tint: "bg-[#F1F4F6] text-ink-muted",
    title: "Checkout isn't live yet",
    tone: "pending",
  },
};

/** An ISO date as a receipt writes it, or nothing where there is none. */
const day = (iso: string | null | undefined): string | null =>
  iso ? formatCreatedOn(new Date(iso)) : null;

/**
 * How a checkout ended.
 *
 * A route of its own rather than a state of the checkout, for two reasons that
 * both come from the payment window: the gateway has to be given a
 * `callback_url` to return to for the flows that redirect, and what it returns
 * to is a fresh page load carrying no router state at all. So the browser's
 * account of the outcome is left in session storage by the checkout and picked
 * up here — see `checkoutOutcome`.
 *
 * That account is a hint and is treated as one. Where there is a subscription
 * id, this screen asks the backend what actually happened and believes that
 * instead: a mandate can succeed after the tab is closed, and the webhook
 * behind that endpoint is the only thing that knows.
 *
 * The three states are deliberately not three variations on "something went
 * wrong". A successful payment is a receipt, and is the only one that says a
 * workspace has been unlocked. A pending one is a promise with nothing to do.
 * A failed one is the only one that offers a retry, and it says what was
 * turned down and by whom, because "payment failed" alone sends people to
 * support rather than to their bank.
 */
const SubscriptionResultPage: React.FC = () => {
  const { cycle } = useParams<{ cycle: string }>();
  const getToken = useTokenGetter();

  // Off the URL rather than off the price list: what was bought is settled, and
  // this screen has to be able to read the outcome of it from the first frame —
  // before any list has arrived, and even if none ever does.
  const cycleId: BillingCycleId | undefined =
    cycle === "monthly" || cycle === "yearly" ? cycle : undefined;

  // The plan itself, for the figures. It is the fallback and not the record:
  // what was actually charged comes off the subscription below wherever there
  // is one, and this only stands in for the paths that never made it that far.
  const { plans } = usePlans();
  const plan = plans.find((candidate) => candidate.id === cycleId);

  // Read once, at mount: the browser's account of what happened, and what to
  // check it against.
  const [stashed] = useState(() => (cycleId ? readResult(cycleId) : null));
  const [outcome, setOutcome] = useState<CheckoutOutcome | null>(
    stashed?.outcome ?? null
  );
  // What the backend says the subscription is, once it has been asked.
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [confirming, setConfirming] = useState(false);

  const subscriptionId = stashed?.subscriptionId ?? null;

  // What the backend says, which outranks what the browser reported. Skipped
  // where there is no backend to ask — see `SUBSCRIPTIONS_LIVE` — and where
  // the flow never got as far as being issued a subscription to ask about.
  useEffect(() => {
    if (!SUBSCRIPTIONS_LIVE || !subscriptionId) return;

    const controller = new AbortController();
    setConfirming(true);

    void fetchSubscription(getToken, subscriptionId, controller.signal)
      .then((record) => {
        if (controller.signal.aborted) return;
        setSubscription(record);
        setOutcome(
          record.status === "ACTIVE" || record.status === "AUTHENTICATED"
            ? { state: "active" }
            : record.status === "HALTED" ||
              record.status === "CANCELLED" ||
              record.status === "EXPIRED"
            ? {
                state: "failed",
                reason: "The mandate was not set up, so nothing was charged.",
              }
            : {
                state: "pending",
                reason:
                  "Your bank is still confirming the payment. We'll email you the moment it's done.",
              }
        );
      })
      .catch(() => {
        // Leave the browser's account standing. It is worse to replace a
        // "payment successful" with an error because one request failed.
      })
      .finally(() => {
        if (!controller.signal.aborted) setConfirming(false);
      });

    return () => controller.abort();
  }, [getToken, subscriptionId]);

  // Nothing to report on: the URL names a cycle that does not exist, or
  // somebody has arrived here without going through a checkout.
  if (!cycleId) return <Navigate to={BILLING_SETTINGS_PATH} replace />;
  if (!outcome) return <Navigate to={BILLING_SETTINGS_PATH} replace />;

  // What the plan would have cost, where the plan is known. `null` while the
  // price list is on its way and if it never comes — which costs this screen a
  // row or two of the receipt and none of what it is for: the heading, the
  // reference and the way onward are all about a payment that has already
  // happened, and none of them is priced.
  const price = plan ? priceBreakdown(plan) : null;
  const productName = plan?.productName ?? PRODUCT_NAME;
  const cycleLabel = plan?.cycleLabel ?? CYCLE_LABELS[cycleId];
  /**
   * What was charged, in the order of who knows best.
   *
   * The record the backend has since made of it outranks everything. Failing
   * that, what the purchase was quoted at on the way through — which is the
   * only figure here that knows what coupon was on the order. The price list is
   * the last resort and does not: it quotes this plan at full price, which is
   * right for most receipts and wrong for a discounted one.
   */
  const amount =
    subscription?.amount ?? stashed?.amount ?? price?.total ?? null;
  const { Icon, tint, title, tone } = FACE[outcome.state];
  const paid = outcome.state === "active";
  const testMode = !!stashed?.testMode;

  /** What the receipt says, out of what is actually known. Rows with no value
   *  are dropped by the card rather than drawn as a dash. */
  const rows: TransactionRow[] = [
    { label: "Plan", value: `${productName} · ${cycleLabel}` },
    {
      label: paid ? "Amount paid" : "Amount due",
      value: amount === null ? null : formatMoney(amount),
    },
    {
      label: "Payment method",
      value: subscription?.method
        ? METHOD_LABELS[subscription.method] ?? subscription.method
        : null,
    },
    { label: "Reference", value: stashed?.paymentId ?? null },
    {
      label: "Next renewal",
      value: paid
        ? day(subscription?.currentEnd) ??
          (price ? formatCreatedOn(price.renewsOn) : null)
        : null,
    },
  ];

  const detail = (): string => {
    switch (outcome.state) {
      case "active":
        return testMode
          ? "Razorpay accepted the payment in test mode. No subscription was created - that needs the backend - so nothing has actually been unlocked."
          : "Your full workspace is unlocked and ready to go.";
      case "pending":
        return outcome.reason;
      case "failed":
        return outcome.reason;
      default:
        return "We can't take payment yet, so nothing has been charged and no subscription has been set up. Nothing you entered was lost.";
    }
  };

  return (
    <div className="px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      {/* No page title: the heading below is what this screen is, and a
          "Billing" above it would only compete with it. */}
      <div className="mx-auto mt-6 flex max-w-[560px] flex-col items-center text-center 3xl:mt-10">
        <span
          className={`flex h-16 w-16 3xl:h-[72px] 3xl:w-[72px] items-center justify-center rounded-full ${tint}`}
        >
          <Icon aria-hidden className="h-7 w-7 3xl:h-8 3xl:w-8" />
        </span>

        <h2 className="mt-5 text-[22px] 3xl:text-[26px] font-medium text-ink">
          {title}
        </h2>

        {confirming ? (
          // The heading is the browser's account and is already on screen;
          // this line is the one thing still being checked.
          <div className="mt-3 flex flex-col items-center gap-2">
            {/* The line still being checked, as one block rather than two. */}
            <SkeletonCard className="h-[38px] w-[300px] max-w-full" />
            <span role="status" className="sr-only">
              Confirming your payment…
            </span>
          </div>
        ) : (
          <p className="mt-2 text-[14px] 3xl:text-[15px] leading-relaxed text-ink-muted">
            {paid && !testMode && (
              <>
                You&rsquo;re now on{" "}
                <span className="font-medium text-ink">
                  {productName}
                </span>
                .{" "}
              </>
            )}
            {detail()}
          </p>
        )}

        {/* Drawn for every outcome that had a price attached to it. A failed
            payment still has to say what it was for and how much — that is
            what somebody checks against their bank statement before deciding
            whether they have in fact been charged twice. */}
        {outcome.state !== "unavailable" && (
          <div className="w-full">
            <TransactionDetails rows={rows} tone={tone} />
          </div>
        )}

        <div className="mt-7 flex w-full flex-col-reverse items-stretch gap-3 sm:flex-row sm:justify-center">
          {paid ? (
            // No Download invoice beside it for now. The receipt above already
            // carries the invoice number, which is the part a support
            // conversation turns on, and every invoice is downloadable from
            // Billing once the backend has raised it. The one thing left here
            // is the way onward, so it leads rather than sits beside a louder
            // button.
            <Link
              to={ROUTES.dashboard}
              className={buttonClasses({
                variant: "primary",
                size: "md",
                className: "sm:min-w-[200px]",
              })}
            >
              Go to dashboard
            </Link>
          ) : (
            <>
              <Link
                to={BILLING_SETTINGS_PATH}
                className={buttonClasses({
                  variant: "outline",
                  size: "md",
                  className: "sm:min-w-[180px]",
                })}
              >
                Back to plans
              </Link>
              {outcome.state !== "pending" && (
                <Link
                  to={billingCheckoutPath(cycleId)}
                  // Cleared on the way out, so the checkout being reopened is
                  // a fresh purchase rather than one that lands straight back
                  // on the result it just left.
                  onClick={() => clearResult(cycleId)}
                  className={buttonClasses({
                    variant: "primary",
                    size: "md",
                    className: "sm:min-w-[180px]",
                  })}
                >
                  Try again
                </Link>
              )}
            </>
          )}
        </div>

        {outcome.state === "pending" && (
          <p className="mt-5 text-[12px] 3xl:text-[13px] text-ink-subtle">
            You can close this page. Nothing is charged until the payment is
            confirmed.
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionResultPage;
